import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import PageBanner from "@/components/PageBanner";
import SectionHeading from "@/components/SectionHeading";
import oliveGrove from "@/assets/olive-grove.webp";
import originExport from "@/assets/origin-to-export.webp";
import exportLogistics from "@/assets/export-logistics.webp";
const marketData = [
  { region: "Europe", countries: "Italy, Spain, Portugal" },
  { region: "The Americas", countries: "USA, Canada" },
  { region: "Asia", countries: "Japan, China" },
  { region: "Global Reach", countries: "Exploring new markets" },
];
const About = () => {
  return (
    <div>
      <PageBanner
        title="About MM2A Tunisian Export"
        subtitle="MM2A Tunisian Export is a Tunisian company specialized in olive oil sourcing, selection, blending and export. Since its foundation in 2022, the company has focused on building a professional and reliable supply structure for serious international buyers."
        backgroundImage={oliveGrove}
      />

      {/* Who We Are */}
      <section className="section-padding">
        <div className="container-max grid md:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHeading title="Who We Are" centered={false} />
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Based in Sfax, Tunisia, MM2A operates with a clear understanding of olive oil sourcing, buyer expectations and export requirements.</p>
              <p>Our objective is not simply to sell olive oil, but to build reliable long-term business relationships with buyers who value clarity, consistency and serious commercial cooperation.</p>
              <p>We started with a supply capacity of 2,000 tonnes in 2022. Today, we handle over 8,000 tonnes per year — a growth driven by trust, execution and the strength of our sourcing network.</p>
            </div>
          </div>
          <div className="bg-muted rounded-lg p-12 flex items-center justify-center">
            <div className="text-center">
              <p className="font-heading text-5xl font-bold text-primary">8,000+</p>
              <p className="text-muted-foreground mt-2">Tonnes per year</p>
            </div>
          </div>
        </div>
      </section>

      {/* From Origin to Export */}
      <section className="section-padding bg-muted">
        <div className="container-max grid md:grid-cols-2 gap-12 items-center">
          <div className="rounded-lg overflow-hidden shadow-lg">
            <img loading="lazy" src={originExport} alt="From origin to export" className="w-full h-full object-cover" loading="lazy" />
          </div>
          <div>
            <SectionHeading title="From Origin to Export" centered={false} />
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Our work begins at the source.</p>
              <p>Through our field presence and production network, we stay close to the realities of Tunisian olive oil — from olive-growing areas and harvest logic to processing, profile selection and export preparation.</p>
              <p>This upstream understanding helps us work with more precision, evaluate supply more accurately and build more relevant commercial offers for professional buyers.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Sourcing Network */}
      <section className="section-padding">
        <div className="container-max">
          <SectionHeading title="Our Sourcing Network" subtitle="One of MM2A's main strengths is its sourcing reach across Tunisia." />
          <div className="grid md:grid-cols-3 gap-8 mt-8">
            {[
              { region: "North Tunisia", desc: "Chetoui dominant — greener, more intense, higher polyphenol potential", color: "bg-olive-dark" },
              { region: "Central Tunisia", desc: "Mixed production base — flexible sourcing and strong volume capability", color: "bg-olive" },
              { region: "South Tunisia", desc: "Chemlali dominant — smoother, balanced profile suited to large-volume supply", color: "bg-olive-light" },
            ].map((r) => (
              <div key={r.region} className={`${r.color} text-primary-foreground p-8 rounded-lg`}>
                <h3 className="font-heading text-xl font-semibold mb-3">{r.region}</h3>
                <p className="text-primary-foreground/80 text-sm leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Markets */}
      <section className="section-padding bg-muted">
        <div className="container-max grid md:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHeading title="Our Markets" centered={false} />
            <p className="text-muted-foreground leading-relaxed mb-6">
              MM2A works with buyers across international markets and remains open to serious commercial opportunities worldwide.
            </p>
<div className="grid grid-cols-2 gap-4 mb-6">
  {marketData.map((m) => (
    <div key={m.region} className="bg-card p-4 rounded-lg text-center border border-border flex flex-col justify-center min-h-[100px] hover:border-olive/50 transition-colors">
      <p className="font-bold text-foreground text-sm md:text-base">{m.region}</p>
      <p className="text-xs text-muted-foreground mt-1">{m.countries}</p>
    </div>
  ))}
</div>
            <p className="text-muted-foreground text-sm">
              We have already supplied recognized international buyers including Sovena, Carapelli and Abasa, among others.
            </p>
          </div>
          <div className="rounded-lg overflow-hidden shadow-lg">
            <img loading="lazy" src={exportLogistics} alt="International export" className="w-full h-full object-cover" loading="lazy" />
          </div>
        </div>
      </section>

      {/* Who We Work With */}
      <section className="section-padding">
        <div className="container-max">
          <SectionHeading title="Who We Work With" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {["Importers", "Bottlers", "Distributors", "Refineries", "Food Manufacturers", "Industrial Processors", "Private Label Operators", "Trading Companies"].map((t) => (
              <div key={t} className="bg-muted p-6 rounded-lg text-center">
                <p className="font-medium text-foreground text-sm">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Working Approach & Mission */}
      <section className="section-padding bg-primary text-primary-foreground">
        <div className="container-max grid md:grid-cols-2 gap-12">
          <div>
            <h2 className="font-heading text-3xl font-bold mb-6">Our Working Approach</h2>
            <p className="text-primary-foreground/80 leading-relaxed mb-4">At MM2A, we believe that serious export business starts with clarity.</p>
            <p className="text-primary-foreground leading-relaxed">Our way of working is based on product understanding, profile matching, transparent communication and practical execution. We focus on building reliable supply relationships — not short-term opportunistic transactions.</p>
          </div>
          <div>
            <h2 className="font-heading text-3xl font-bold mb-6">Our Mission</h2>
            <p className="text-primary-foreground/80 leading-relaxed">
Our mission is to represent Tunisian olive oil through a serious, structured and export-oriented business model — having supplied recognized international buyers including Sovena, Carapelli and Abasa, with over 8,000 tonnes handled annually across Europe and beyond.            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
